import * as React from 'react'
import { ApolloError } from '@apollo/client'
import styled from 'styled-components'
import { ValidationError } from 'joi'
import { useTranslation } from 'i18n'

type Props = { children: Error | string | Object | ValidationError }
type Error = ApolloError & { details: { message: string }[] }

const ErrorComponent = ({ children: error }: Props) => {
  const { t } = useTranslation('components')

  if (!error) return null
  if (typeof error === 'string') return <Content>{error}</Content>

  // joi validastion object
  if (error instanceof ValidationError) {
    const messages = error.details.map(e => e.message)
    return <Content>{messages.join('\n')}</Content>
  }

  if (error instanceof ApolloError) {
    if (error.networkError) {
      // local resolver validation error
      const messages = getValidationMessages(error.networkError, t)
      return <Content>{messages.join('\n')}</Content>
    }
    if (error.graphQLErrors) {
      const messages = error.graphQLErrors.map(e => {
        if (e.extensions.code === 'BAD_USER_INPUT') {
          if (e.extensions.exception.details)
            return e.extensions.exception.details.map(e => e.message).join('\n')

          if (e.extensions.exception.code)
            return t('stripe:errorCode.' + e.extensions.exception.code)

          return t('errorOccured')
        }
        return e.message
      })
      return <Content>{messages.join('\n')}</Content>
    }
  }

  if (error instanceof Error) {
    // for FirebaseError
    if (error['code'] && error['code'].startsWith('auth/'))
      return (
        <Content>
          {t(['firebase:auth.errors.' + error['code'], 'errorOccured'])}
        </Content>
      )

    return <Content>{error.message}</Content>
  }

  if (error instanceof Object) {
    const errors = Object.values(error) || []
    const messages = errors.map(e => {
      if (e['message']) return e['message']
      return e
    })

    if (!messages.length) return null
    return <Content>{Object.values(messages).join('\n')}</Content>
  }

  return null
}

export default ErrorComponent

const Content = styled.div`
  color: red;
  white-space: pre-wrap;
`

function getValidationMessages(validationError, t) {
  if (validationError.details)
    return validationError.details.map(d => {
      return d.message
    })

  if (validationError.message) return [t('commonValidationError')]

  return []
}
